import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@material-ui/core';
import { MenuBook, OpenInNew, ExpandMore } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const DOCUMENTATION_URL = 'https://docs.kealabs.com/platform/prestashop/customization/card-template/';

const FIELD_GROUPS = [
  {
    id: 'product',
    label: "ui.search.customization.cardTemplate.templateHelpPanel.product",
    description: "ui.search.customization.cardTemplate.templateHelpPanel.identityLinksImagesAndSku",
    fields: [
      { path: '{{ product.id }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.productIdentifier" },
      { path: '{{ product.url }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.productPageUrl" },
      { path: '{{ product.title }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.productName" },
      { path: '{{ product.image_url }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.primaryImageUrl" },
      { path: '{{#product.has_image}} … {{/product.has_image}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.showContentWhenAnImageExists" },
      { path: '{{ product.vendor_code }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.vendorOrSkuReference" },
      { path: '{{#product.has_vendor_code}} … {{/product.has_vendor_code}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.showContentWhenAnSkuExists" },
    ],
  },
  {
    id: 'pricing',
    label: "ui.search.customization.cardTemplate.templateHelpPanel.pricing",
    description: "ui.search.customization.cardTemplate.templateHelpPanel.pricesDiscountsAndCurrency",
    fields: [
      { path: '{{ product.price_text }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.formattedDisplayPrice" },
      { path: '{{ product.original_price_text }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.formattedPriceBeforeDiscount" },
      { path: '{{ product.original_price }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.rawPriceBeforeDiscount" },
      { path: '{{#product.has_discount}} … {{/product.has_discount}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.showContentForDiscountedProducts" },
      { path: '{{ product.discount_percent }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.discountPercentage" },
      { path: '{{ product.currency }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.currencyCode" },
    ],
  },
  {
    id: 'availability',
    label: "ui.search.customization.cardTemplate.templateHelpPanel.availability",
    description: "ui.search.customization.cardTemplate.templateHelpPanel.stockStatusAndQuantity",
    fields: [
      { path: '{{#product.available}} … {{/product.available}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.showContentWhenTheProductIsInStock" },
      { path: '{{ product.count }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.stockQuantity" },
      { path: '{{ product.unit }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.stockUnitLabel" },
    ],
  },
  {
    id: 'actions',
    label: "ui.search.customization.cardTemplate.templateHelpPanel.productActions",
    description: "ui.search.customization.cardTemplate.templateHelpPanel.availableCardInteractions",
    fields: [
      { path: '{{#product.can_open}} … {{/product.can_open}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.productPageCanBeOpened" },
      { path: '{{#product.can_add_to_cart}} … {{/product.can_add_to_cart}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.addToCartIsAvailable" },
      { path: '{{#product.can_find_similar}} … {{/product.can_find_similar}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.visualSimilaritySearchIsAvailable" },
    ],
  },
  {
    id: 'labels',
    label: "ui.search.customization.cardTemplate.templateHelpPanel.interfaceLabels",
    description: "ui.search.customization.cardTemplate.templateHelpPanel.localizedButtonAndFieldText",
    fields: [
      { path: '{{ labels.add_to_cart }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.addToCartLabel" },
      { path: '{{ labels.find_similar }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.findSimilarLabel" },
      { path: '{{ labels.vendor_code }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.skuLabel" },
    ],
  },
  {
    id: 'display',
    label: "ui.search.customization.cardTemplate.templateHelpPanel.displayContext",
    description: "ui.search.customization.cardTemplate.templateHelpPanel.surfaceModeBreakpointAndLayout",
    fields: [
      { path: '{{ ui.source }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.currentSearchInterface" },
      { path: '{{ ui.mode }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.previewOrProductionMode" },
      { path: '{{ ui.breakpoint }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.currentResponsiveBreakpoint" },
      { path: '{{ ui.card_type }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.currentCardLayout" },
      { path: '{{#ui.is_standard}} … {{/ui.is_standard}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.showContentForTheStandardLayout" },
      { path: '{{#ui.is_horizontal}} … {{/ui.is_horizontal}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.showContentForTheHorizontalLayout" },
      { path: '{{#ui.is_simple}} … {{/ui.is_simple}}', label: "ui.search.customization.cardTemplate.templateHelpPanel.showContentForTheSimpleLayout" },
      { path: '{{ ui.position }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.positionInTheResultsGrid" },
      { path: '{{ ui.ratio }}', label: "ui.search.customization.cardTemplate.templateHelpPanel.imageAspectRatio" },
    ],
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  heading: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  icon: {
    width: 36,
    height: 36,
    flex: '0 0 36px',
    borderRadius: 8,
    background: 'var(--kea-color-primary-soft)',
    color: 'var(--kea-color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    margin: 0,
    color: '#1e2248',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.35,
  },
  description: {
    margin: theme.spacing(0.5, 0, 0),
    color: '#66708a',
    fontSize: 12,
    lineHeight: 1.45,
  },
  fieldCatalog: {
    maxHeight: 300,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    border: '1px solid #e6eeeb',
    borderRadius: 8,
    background: '#fff',
    scrollbarGutter: 'stable',
  },
  accordion: {
    margin: '0 !important',
    boxShadow: 'none',
    borderBottom: '1px solid #e6eeeb',
    '&:last-child': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    minHeight: '52px !important',
    padding: theme.spacing(0, 1.5),
    '& .MuiAccordionSummary-content': {
      minWidth: 0,
      margin: '8px 0 !important',
    },
    '& .MuiAccordionSummary-expandIcon': {
      padding: 8,
      color: '#66708a',
    },
  },
  groupLabel: {
    margin: 0,
    color: '#1e2248',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.35,
  },
  groupDescription: {
    margin: '2px 0 0',
    color: '#7b8499',
    fontSize: 11,
    lineHeight: 1.35,
  },
  accordionDetails: {
    padding: theme.spacing(0, 1.5, 1.5),
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 3,
  },
  fieldPath: {
    maxWidth: '100%',
    padding: '2px 6px',
    boxSizing: 'border-box',
    borderRadius: 4,
    background: '#eef4f2',
    color: '#1e2248',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: 11,
    lineHeight: 1.45,
    overflowWrap: 'anywhere',
  },
  fieldLabel: {
    color: '#66708a',
    fontSize: 11,
    lineHeight: 1.35,
  },
  rule: {
    margin: 0,
    color: '#66708a',
    fontSize: 12,
    lineHeight: 1.5,
  },
  docsButton: {
    alignSelf: 'flex-start',
    minHeight: 40,
    padding: theme.spacing(0, 2),
    borderColor: '#cfded8',
    borderRadius: 999,
    color: '#226b51',
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'none',
  },
}));

const TemplateHelpPanel = () => {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const [expanded, setExpanded] = useState('product');

  const handleChange = (groupId) => (_event, isExpanded) => {
    setExpanded(isExpanded ? groupId : false);
  };

  return (
    <Box className={classes.root} data-testid="template-help-panel">
      <div className={classes.heading}>
        <div className={classes.icon} aria-hidden="true">
          <MenuBook fontSize="small" />
        </div>
        <div>
          <Typography component="h2" className={classes.title}><IntlMessages id="ui.search.customization.cardTemplate.templateHelpPanel.templateFields" /></Typography>
          <p className={classes.description}>
            <IntlMessages id="ui.search.customization.cardTemplate.templateHelpPanel.browseTheValuesAvailableInCustomMustacheTemplates" />
          </p>
        </div>
      </div>

      <div className={classes.fieldCatalog} data-testid="template-field-catalog">
        {FIELD_GROUPS.map((group) => (
          <Accordion
            key={group.id}
            className={classes.accordion}
            expanded={expanded === group.id}
            onChange={handleChange(group.id)}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore fontSize="small" />}
              className={classes.accordionSummary}
              aria-controls={`template-fields-${group.id}`}
              id={`template-fields-${group.id}-header`}
            >
              <div>
                <p className={classes.groupLabel}>{t(group.label)}</p>
                <p className={classes.groupDescription}>{t(group.description)}</p>
              </div>
            </AccordionSummary>
            <AccordionDetails
              id={`template-fields-${group.id}`}
              className={classes.accordionDetails}
            >
              {group.fields.map((field) => (
                <div className={classes.field} key={field.path}>
                  <code className={classes.fieldPath}>{field.path}</code>
                  <span className={classes.fieldLabel}>{t(field.label)}</span>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

      <p className={classes.rule}>
        <IntlMessages id="ui.search.customization.cardTemplate.templateHelpPanel.customInteractiveElementsMustUseTheDocumented" />{' '}<code>data-kea-action</code>{' '}<IntlMessages id="ui.search.customization.cardTemplate.templateHelpPanel.values" />
      </p>

      <Button
        component="a"
        href={DOCUMENTATION_URL}
        target="_blank"
        rel="noopener noreferrer"
        variant="outlined"
        endIcon={<OpenInNew fontSize="small" />}
        className={classes.docsButton}
        data-testid="card-template-documentation"
      >
        <IntlMessages id="ui.search.customization.cardTemplate.templateHelpPanel.fullDocumentation" />
      </Button>
    </Box>
  );
};

export default TemplateHelpPanel;
